import React, { useEffect, useRef, useMemo, useCallback, useState } from 'react';
import { useLanguage } from '../../../../../context/LanguageContext';
import { Link, useNavigate } from 'react-router-dom';
import '../style/product-details.css';

// Interface for product data
interface ProductData {
    id: string;
    categoryId: number | string;
    productNamePt?: string;
    productNameEn?: string;
    productBrand?: string;
    productImages?: string[];
    productCode?: string;
    productPrice?: string | number;
}

const ProductDetailsSection: React.FC = () => {
    const { t, language, getTranslation } = useLanguage();
    const navigate = useNavigate();
    const containerRef = useRef<HTMLDivElement>(null);
    const [currentProduct, setCurrentProduct] = useState<ProductData | null>(null);
    
    // Type assertion for nested translation access
    const translation = useMemo(() => t as any, [t, language]);
    const productDetailsTranslations = useMemo(() => translation.PRODUCT_DETAILS || t.PRODUCT_DETAILS, [translation, language]);
    
    // Get go back text using getTranslation function for reliable access
    const goBackText = useMemo(() => {
        const goBack = getTranslation('BUTTONS.goBack') || getTranslation('BUTTONS.back');
        return goBack || (language === 'pt' ? 'Voltar' : 'Go Back');
    }, [getTranslation, language]);

    // Memoized back button handler to prevent multiple clicks issue
    const handleGoBack = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Check if desktop (screen width > 991px)
        const isDesktop = window.innerWidth > 991;
        
        if (isDesktop) {
            // On desktop: always go to store page
            navigate('/loja');
        } else {
            // On mobile: go back in history or fallback to store
            if (window.history.length > 1) {
                navigate(-1);
            } else {
                navigate('/loja');
            }
        }
    }, [navigate]);

    // Share functionality
    const getShareData = useCallback(() => {
        const currentUrl = window.location.href;
        const titleElement = document.getElementById('product-title');
        const imageElement = document.getElementById('main-product-image') as HTMLImageElement;
        
        const productName = titleElement?.textContent?.trim() || 'Product';
        const productImage = imageElement?.src || '';
        
        return { currentUrl, productName, productImage };
    }, []);

    const handleShare = useCallback((platform: 'facebook' | 'twitter' | 'linkedin' | 'pinterest') => {
        const { currentUrl, productName, productImage } = getShareData();
        
        let shareUrl = '';
        const width = 600;
        const height = 400;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;
        
        switch (platform) {
            case 'facebook':
                shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`;
                break;
            case 'twitter':
                shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(currentUrl)}&text=${encodeURIComponent(productName)}`;
                break;
            case 'linkedin':
                shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`;
                break;
            case 'pinterest':
                shareUrl = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(currentUrl)}&description=${encodeURIComponent(productName)}${productImage ? `&media=${encodeURIComponent(productImage)}` : ''}`;
                break;
        }
        
        if (shareUrl) {
            window.open(
                shareUrl,
                'Share',
                `width=${width},height=${height},left=${left},top=${top},toolbar=0,menubar=0,location=0,status=0,scrollbars=1,resizable=1`
            );
        }
    }, [getShareData]);

    // Listen for product data from the product-details-logic script
    useEffect(() => {
        const checkProductData = () => {
            const windowAny = window as any;
            if (windowAny.currentProductData) {
                const product = windowAny.currentProductData;
                setCurrentProduct(product);
            }
        };

        // Check immediately
        checkProductData();

        // Also check periodically as product data loads asynchronously
        const interval = setInterval(checkProductData, 500);
        
        // Stop checking after product is found or after 10 seconds
        const timeout = setTimeout(() => clearInterval(interval), 10000);

        return () => {
            clearInterval(interval);
            clearTimeout(timeout);
        };
    }, []);

    // Handle Request Budget button click
    const handleRequestBudget = useCallback(() => {
        if (!currentProduct) return;

        // Store the selected product in localStorage for the budget page
        const budgetProduct = {
            id: currentProduct.id,
            productNamePt: currentProduct.productNamePt,
            productNameEn: currentProduct.productNameEn,
            productBrand: currentProduct.productBrand,
            productImage: currentProduct.productImages?.[0] || '',
            productCode: currentProduct.productCode,
            categoryId: currentProduct.categoryId
        };

        // Get existing budget products or create new array
        const existingProducts = JSON.parse(localStorage.getItem('budgetProducts') || '[]');
        
        // Check if product already exists
        const exists = existingProducts.some((p: any) => p.id === budgetProduct.id);
        if (!exists) {
            existingProducts.push(budgetProduct);
            localStorage.setItem('budgetProducts', JSON.stringify(existingProducts));
        }

        // Set flag to indicate we're coming from product details with a newly added product
        localStorage.setItem('budgetProductAdded', JSON.stringify({
            productId: budgetProduct.id,
            timestamp: Date.now()
        }));

        // Navigate to budget page
        navigate('/orcamento');
    }, [currentProduct, navigate]);

    useEffect(() => {
        // Initialize tabs functionality
        const initializeTabs = () => {
            const tabButtons = document.querySelectorAll('.tab-btn');
            const tabs = document.querySelectorAll('.tab');

            tabButtons.forEach(button => {
                button.addEventListener('click', function(this: HTMLElement) {
                    // Remove active class from all buttons and tabs
                    tabButtons.forEach(btn => btn.classList.remove('active-btn'));
                    tabs.forEach(tab => tab.classList.remove('active-tab'));

                    // Add active class to clicked button
                    this.classList.add('active-btn');

                    // Show corresponding tab
                    const tabId = this.getAttribute('data-tab');
                    if (tabId) {
                        const targetTab = document.querySelector(tabId);
                        if (targetTab) {
                            targetTab.classList.add('active-tab');
                        }
                    }
                });
            });
        };

        const timer = setTimeout(initializeTabs, 500);
        return () => clearTimeout(timer);
    }, []);

    return (
        <>
            {/* Page Header */}
            <section className="page-header product-details-page-header">
                <div
                    className="page-header__bg"
                    style={{
                        backgroundImage: 'url(/assets/logo/store/loja_2.webp)',
                        backgroundSize: 'cover',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center',
                        height: '380px'
                    }}
                ></div>
                <div className="container">
                    <div className="page-header__inner">
                        <h1>{productDetailsTranslations?.pageTitle || 'Product Details'}</h1>
                        <div className="thm-breadcrumb__inner">
                            <ul className="thm-breadcrumb list-unstyled">
                                <li><Link to="/">{productDetailsTranslations?.home || 'Home'}</Link></li>
                                <li><span className="fas fa-angle-right"></span></li>
                                <li><Link to="/loja">{productDetailsTranslations?.store || 'Store'}</Link></li>
                                <li><span className="fas fa-angle-right"></span></li>
                                <li>{productDetailsTranslations?.pageTitle || 'Product Details'}</li>
                            </ul>
                        </div>
                        <button 
                            type="button"
                            className="product-details__back-button"
                            onClick={handleGoBack}
                            aria-label="Go back"
                        >
                            <i className="fas fa-arrow-left"></i>
                            <span>{goBackText}</span>
                        </button>
                    </div>
                </div>
            </section>

            {/* Product Details */}
            <section className="product-details" ref={containerRef}>
                <div className="container">
                    <div className="row">
                        <div className="col-lg-6 col-xl-6">
                            <div className="product-details__left">
                                <div className="product-details__left-inner">
                                    <div className="product-details__content-box">
                                        <div className="swiper-container" id="shop-details-one__carousel">
                                            <div className="swiper-wrapper">
                                                <div className="swiper-slide">
                                                    <div className="product-details__img">
                                                        <img
                                                            id="main-product-image"
                                                            src="/assets/images/shop/product-details-img-1.jpg"
                                                            alt={productDetailsTranslations?.productImage || 'Product Image'}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-lg-6 col-xl-6">
                            <div className="product-details__right">
                                <div className="product-details__top">
                                    <h3 className="product-details__title" id="product-title">
                                        {productDetailsTranslations?.loadingProduct || 'Loading product...'}
                                    </h3>
                                </div>
                                <div className="product-details__reveiw">
                                    <i className="fa fa-star"></i>
                                    <i className="fa fa-star"></i>
                                    <i className="fa fa-star"></i>
                                    <i className="fa fa-star"></i>
                                    <i className="fa fa-star"></i>
                                    <span>{productDetailsTranslations?.customerReviews || 'Customer reviews'}</span>
                                </div>
                                <div className="product-details__content">
                                    <p className="product-details__content-text1" id="product-description">
                                        {productDetailsTranslations?.loadingDescription || 'Loading product description...'}
                                    </p>
                                    <p className="product-details__content-text2" id="product-code">
                                        {productDetailsTranslations?.loadingCode || 'Loading product code...'}
                                    </p>
                                </div>
                                <div className="product-details__inner">
                                    <div className="product-details__buttons-boxes">
                                        <div className="product-details__buttons-1">
                                            <Link to="/lista-desejos" className="thm-btn">
                                                {productDetailsTranslations?.addToWishlist || 'Add to Wishlist'}{' '}
                                                <span>
                                                    <i className="icon-next"></i>
                                                </span>
                                            </Link>
                                        </div>
                                        {/* Request Budget button - for all products */}
                                        {currentProduct && (
                                            <div className="product-details__buttons-2">
                                                <button 
                                                    type="button"
                                                    className="thm-btn thm-btn--budget"
                                                    onClick={handleRequestBudget}
                                                >
                                                    {language === 'pt' ? 'Pedir Orçamento' : 'Request Budget'}{' '}
                                                    <span>
                                                        <i className="icon-next"></i>
                                                    </span>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="product-details__social">
                                    <div className="title">
                                        <h3>{productDetailsTranslations?.shareWithFriends || 'Share with friends:'}</h3>
                                    </div>
                                    <div className="product-details__social-link">
                                        <a 
                                            href="#" 
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleShare('facebook');
                                            }}
                                            aria-label="Share on Facebook"
                                        >
                                            <span className="icon-facebook-app-symbol"></span>
                                        </a>
                                        <a 
                                            href="#" 
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleShare('twitter');
                                            }}
                                            aria-label="Share on Twitter"
                                        >
                                            <span className="icon-twitter"></span>
                                        </a>
                                        <a 
                                            href="#" 
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleShare('linkedin');
                                            }}
                                            aria-label="Share on LinkedIn"
                                        >
                                            <span className="icon-linkedin"></span>
                                        </a>
                                        <a 
                                            href="#" 
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleShare('pinterest');
                                            }}
                                            aria-label="Share on Pinterest"
                                        >
                                            <span className="icon-pinterest"></span>
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Product Description Tabs */}
            <section className="product-description">
                <div className="container">
                    <div className="product-details__description">
                        <div className="product-details__main-tab-box tabs-box">
                            <ul className="tab-buttons clearfix list-unstyled">
                                <li data-tab="#description" className="tab-btn active-btn">
                                    <span>{productDetailsTranslations?.description || 'Description'}</span>
                                </li>
                                <li data-tab="#additional-information" className="tab-btn">
                                    <span>{productDetailsTranslations?.additionalInformation || 'Additional information'}</span>
                                </li>
                                <li data-tab="#reviews" className="tab-btn">
                                    <span>{productDetailsTranslations?.reviews || 'Reviews'}</span>
                                </li>
                            </ul>
                            <div className="tabs-content">
                                {/* Description Tab */}
                                <div className="tab active-tab" id="description">
                                    <div className="product-details__tab-content-inner">
                                        <div className="product-details__description-content">
                                            <p
                                                className="product-details__description-text-1"
                                                id="product-full-description"
                                            >
                                                {productDetailsTranslations?.loadingDetails || 'Loading product details...'}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Additional Information Tab */}
                                <div className="tab" id="additional-information">
                                    <div className="product-details__tab-content-inner">
                                        <div className="product-details__additional-information-content">
                                            <p className="product-details__additional-information-text-1">
                                                <strong>{productDetailsTranslations?.brand || 'Brand:'}</strong> <span id="product-brand-info">{productDetailsTranslations?.loading || 'Loading...'}</span>
                                                <br />
                                                <strong>{productDetailsTranslations?.category || 'Category:'}</strong> <span id="product-category-info">{productDetailsTranslations?.loading || 'Loading...'}</span>
                                                <br />
                                                <strong>{productDetailsTranslations?.code || 'Code:'}</strong> <span id="product-code-info">{productDetailsTranslations?.loading || 'Loading...'}</span>
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Reviews Tab */}
                                <div className="tab" id="reviews">
                                    <div className="product-details__tab-content-inner">
                                        <div className="review-one">
                                            <div className="comments-area">
                                                <div className="review-one__title">
                                                    <h3>{productDetailsTranslations?.productReviews || 'Product Reviews'}</h3>
                                                </div>
                                                <p>{productDetailsTranslations?.noReviewsAvailable || 'No reviews available for this product yet.'}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default ProductDetailsSection;

